<template>
  <div class="slideout-panel">
    <div class="slideout-header">
      <h3 class="text-xl font-semibold">
        Send Templated Message
      </h3>
      <button
        class="close-button"
        @click="close">
        <i class="fas fa-times" />
      </button>
    </div>

    <div class="slideout-body">
      <!-- Organization Info -->
      <div class="org-info-box">
        <h4 class="text-sm font-medium text-gray-700 mb-2">
          Organization
        </h4>
        <p class="text-base font-semibold">
          {{ organizationName }}
        </p>
        <p
          v-if="customerOwner && customerOwner !== 'linda'"
          class="text-sm text-amber-600 mt-2">
          <i class="fas fa-exclamation-triangle mr-1" />
          This is a {{ formatCustomerOwner(customerOwner) }} customer - messages can only be sent to Linda customers
        </p>
      </div>

      <!-- Template Selection -->
      <div class="form-section">
        <label class="form-label">Select Message Template</label>
        <select
          v-model="selectedTemplateId"
          class="form-select"
          @change="onTemplateChange">
          <option
            value=""
            disabled>
            Choose a template...
          </option>
          <option
            v-for="template in templates"
            :key="template.id"
            :value="template.id">
            {{ template.name }}
          </option>
        </select>
        <p
          v-if="selectedTemplate"
          class="mt-2 text-sm text-gray-600">
          {{ selectedTemplate.description }}
        </p>
      </div>

      <!-- Recipient Selection -->
      <div
        v-if="selectedTemplate"
        class="form-section">
        <label class="form-label">Recipients</label>
        <div class="space-y-2">
          <label
            v-if="primaryContact && primaryContact.email"
            class="flex items-center">
            <input
              v-model="recipients.primary"
              type="checkbox"
              class="form-checkbox">
            <span class="ml-2">
              <strong>Primary:</strong> {{ primaryContact.name }} ({{ primaryContact.email }})
            </span>
          </label>
          <label
            v-else-if="primaryContact"
            class="flex items-center opacity-50">
            <input
              type="checkbox"
              disabled
              class="form-checkbox">
            <span class="ml-2">
              <strong>Primary:</strong> {{ primaryContact.name }} (No email address)
            </span>
          </label>

          <label
            v-if="secondaryContact && secondaryContact.email"
            class="flex items-center">
            <input
              v-model="recipients.secondary"
              type="checkbox"
              class="form-checkbox">
            <span class="ml-2">
              <strong>Secondary:</strong> {{ secondaryContact.name || 'Secondary Contact' }} ({{ secondaryContact.email }})
            </span>
          </label>
          <label
            v-else-if="secondaryContact && secondaryContact.name"
            class="flex items-center opacity-50">
            <input
              type="checkbox"
              disabled
              class="form-checkbox">
            <span class="ml-2">
              <strong>Secondary:</strong> {{ secondaryContact.name }} (No email address)
            </span>
          </label>
        </div>
        <p
          v-if="!hasAnyRecipients"
          class="mt-2 text-sm text-red-600">
          No contacts with email addresses available
        </p>
      </div>

      <!-- Scheduling Options -->
      <div
        v-if="selectedTemplate && hasSelectedRecipients"
        class="form-section">
        <label class="form-label">Send Timing</label>
        <div class="space-y-2">
          <label class="flex items-center">
            <input
              v-model="sendTiming"
              type="radio"
              value="now"
              class="form-radio">
            <span class="ml-2">Send Now</span>
          </label>
          <label class="flex items-center">
            <input
              v-model="sendTiming"
              type="radio"
              value="scheduled"
              class="form-radio">
            <span class="ml-2">Schedule for Later</span>
          </label>
        </div>
        <div
          v-if="sendTiming === 'scheduled'"
          class="mt-3 space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Date & Time</label>
            <input
              v-model="scheduledFor"
              type="datetime-local"
              class="form-input"
              :min="minDateTime">
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Timezone</label>
            <select
              v-model="timezone"
              class="form-select">
              <option value="America/New_York">
                Eastern Time (ET)
              </option>
              <option value="America/Chicago">
                Central Time (CT)
              </option>
              <option value="America/Denver">
                Mountain Time (MT)
              </option>
              <option value="America/Los_Angeles">
                Pacific Time (PT)
              </option>
              <option value="America/Phoenix">
                Arizona Time
              </option>
              <option value="Pacific/Honolulu">
                Hawaii Time
              </option>
              <option value="UTC">
                UTC
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button
          v-if="canPreview"
          class="btn btn-secondary"
          :disabled="loading"
          @click="preview">
          <i class="fas fa-eye mr-2" />
          Preview
        </button>
        <button
          v-if="canSend"
          class="btn btn-primary"
          :disabled="loading || !isValid"
          @click="send">
          <i
            v-if="loading"
            class="fas fa-spinner fa-spin mr-2" />
          <i
            v-else
            class="fas fa-paper-plane mr-2" />
          {{ sendTiming === 'scheduled' ? 'Schedule' : 'Send Now' }}
        </button>
      </div>

      <!-- Message History -->
      <div
        v-if="messageHistory.length > 0"
        class="form-section">
        <h4 class="text-sm font-medium text-gray-700 mb-2">
          Recent Messages ({{ messageHistory.length }})
        </h4>
        <div class="message-history">
          <div
            v-for="msg in messageHistory"
            :key="msg.id"
            class="message-history-item">
            <div class="flex justify-between items-start mb-2">
              <div class="flex-1">
                <p class="text-sm font-semibold text-gray-900">
                  {{ msg.templateName || getEventTypeLabel(msg.eventType) }}
                </p>
                <p class="text-xs text-gray-500 mt-0.5">
                  <i class="fas fa-tag mr-1 text-gray-400" />
                  Type: {{ getEventTypeLabel(msg.eventType) }}
                </p>
                <p class="text-xs text-gray-600 mt-1">
                  <i class="fas fa-envelope mr-1 text-gray-400" />
                  Subject: {{ msg.renderedSubject || 'No subject' }}
                </p>
              </div>
              <span
                class="text-xs px-2 py-1 rounded-full font-medium"
                :class="getStatusClass(msg.status)">
                {{ msg.status }}
              </span>
            </div>

            <div class="space-y-1">
              <p class="text-xs text-gray-600">
                <i class="fas fa-users mr-1 text-gray-400" />
                Recipients: {{ formatRecipientsDetailed(msg.recipients) }}
              </p>

              <p class="text-xs text-gray-600">
                <i class="fas fa-clock mr-1 text-gray-400" />
                {{ getMessageDateLabel(msg) }}: {{ formatDate(getMessageDate(msg)) }}
              </p>

              <p
                v-if="msg.sentByUserName"
                class="text-xs text-gray-600">
                <i class="fas fa-user mr-1 text-gray-400" />
                Sent by: {{ msg.sentByUserName }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <MessagePreviewModal
      v-if="showPreview"
      :preview-data="previewData"
      @close="showPreview = false"
      @send="send" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '@/init/axios'
import MessagePreviewModal from './message_preview_modal.vue'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  organizationName: {
    type: String,
    required: true
  },
  customerOwner: {
    type: String,
    default: null
  },
  primaryContact: {
    type: Object,
    default: null
  },
  secondaryContact: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'messageSent', 'on-close-panel'])

// State
const loading = ref(false)
const templates = ref([])
const selectedTemplateId = ref('')
const recipients = ref({
  primary: false,
  secondary: false
})
const sendTiming = ref('now')
const scheduledFor = ref('')
const timezone = ref('America/New_York') // Default to Eastern Time
const showPreview = ref(false)
const previewData = ref(null)
const messageHistory = ref([])

// Computed
const selectedTemplate = computed(() => templates.value.find((t) => t.id === selectedTemplateId.value))

const hasAnyRecipients = computed(() => (props.primaryContact?.email || props.secondaryContact?.email))

const hasSelectedRecipients = computed(() => recipients.value.primary || recipients.value.secondary)

const canPreview = computed(() => selectedTemplate.value && hasSelectedRecipients.value)

const canSend = computed(() => canPreview.value && (sendTiming.value === 'now' || scheduledFor.value) && props.customerOwner === 'linda')

const isValid = computed(() => {
  if (!selectedTemplate.value) return false
  if (!hasSelectedRecipients.value) return false
  if (sendTiming.value === 'scheduled' && !scheduledFor.value) return false
  if (props.customerOwner !== 'linda') return false
  return true
})

const minDateTime = computed(() => {
  const now = new Date()
  now.setMinutes(now.getMinutes() + 5) // Minimum 5 minutes from now
  return now.toISOString().slice(0, 16)
})

// Methods
const close = () => {
  emit('on-close-panel')
  emit('close')
}

const fetchTemplates = async() => {
  try {
    const response = await axios.get('/ajax/message_templates')
    if (response.data.success) {
      templates.value = response.data.templates
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching templates:', error)
    mdtoast.error('Failed to load message templates', { duration: 3000 })
  }
}

const fetchMessageHistory = async() => {
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/messages/message_history`)
    if (response.data.success) {
      messageHistory.value = response.data.messages || []
      // eslint-disable-next-line no-console
      console.log('Fetched message history:', messageHistory.value.length, 'messages')
    } else {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch message history:', response.data)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching message history:', error)
  }
}

const onTemplateChange = () => {
  // Auto-select recipients if they have emails
  if (props.primaryContact?.email) {
    recipients.value.primary = true
  }
  if (props.secondaryContact?.email) {
    recipients.value.secondary = true
  }
}

const preview = async() => {
  if (!canPreview.value) return

  loading.value = true
  try {
    const response = await axios.post(`/ajax/organizations/${props.organizationId}/messages/preview`, {
      template_id: selectedTemplateId.value,
      recipients: recipients.value
    })

    if (response.data.success) {
      previewData.value = response.data.preview
      showPreview.value = true
    } else {
      mdtoast.error(response.data.error || 'Failed to generate preview', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating preview:', error)
    mdtoast.error('Failed to generate preview', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const send = async() => {
  if (!canSend.value) return

  loading.value = true
  try {
    const payload = {
      template_id: selectedTemplateId.value,
      recipients: recipients.value,
      scheduled_for: sendTiming.value === 'scheduled' ? scheduledFor.value : null,
      timezone: sendTiming.value === 'scheduled' ? timezone.value : null
    }

    const response = await axios.post(`/ajax/organizations/${props.organizationId}/messages/send_message`, payload)

    if (response.data.success) {
      const message = sendTiming.value === 'scheduled' ?
        'Message scheduled successfully' :
        'Message sent successfully'
      mdtoast.success(message, { duration: 3000 })
      emit('messageSent', response.data.message)
      close()
    } else {
      mdtoast.error(response.data.error || 'Failed to send message', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error sending message:', error)
    mdtoast.error('Failed to send message', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const formatRecipientsDetailed = (recipientData) => {
  if (!recipientData) return 'No recipients'

  const list = []
  // Handle both formats in case of inconsistency
  if (recipientData.primary || recipientData.primary) {
    const primaryName = props.primaryContact?.name || 'Primary Contact'
    list.push(`${primaryName} (Primary)`)
  }
  if (recipientData.secondary || recipientData.secondary) {
    const secondaryName = props.secondaryContact?.name || 'Secondary Contact'
    list.push(`${secondaryName} (Secondary)`)
  }
  return list.length > 0 ? list.join(', ') : 'No recipients'
}

const formatDate = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
  return d.toLocaleString('en-US', options)
}

// axios auto-converts snake_case to camelCase
const getMessageDate = (msg) => msg.sentAt || msg.scheduledFor || msg.createdAt

const getMessageDateLabel = (msg) => {
  if (msg.sentAt) return 'Sent'
  if (msg.scheduledFor) return 'Scheduled for'
  return 'Created'
}

const getEventTypeLabel = (eventType) => {
  const labels = {
    late_payment: 'Late Payment Reminder',
    running_low_on_content: 'Low Content Alert',
    out_of_content: 'Out of Content Alert',
    fb_disconnected: 'Facebook Disconnection Alert'
  }
  return labels[eventType] || eventType
}

const getStatusClass = (status) => {
  switch (status) {
    case 'sent':
      return 'bg-green-100 text-green-800'
    case 'scheduled':
      return 'bg-blue-100 text-blue-800'
    case 'failed':
      return 'bg-red-100 text-red-800'
    case 'cancelled':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-yellow-100 text-yellow-800'
  }
}

const formatCustomerOwner = (owner) => {
  switch (owner) {
    case 'pin_parrot':
      return 'Pin Parrot'
    case 'constant_social':
      return 'Constant Social'
    default:
      return owner ? owner.charAt(0).toUpperCase() + owner.slice(1) : ''
  }
}

// Lifecycle
onMounted(() => {
  fetchTemplates()
  fetchMessageHistory()
})
</script>

<style scoped>
.slideout-panel {
  @apply h-full flex flex-col bg-white;
}

.slideout-header {
  @apply flex items-center justify-between px-6 py-4 border-b border-gray-200;
}

.close-button {
  @apply p-2 text-gray-400 hover:text-gray-600 transition-colors;
}

.slideout-body {
  @apply flex-1 overflow-y-auto px-6 py-4 space-y-6;
}

.org-info-box {
  @apply bg-gray-50 rounded-lg p-4;
}

.form-section {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-select,
.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
         transition-colors duration-200;
}

.form-checkbox,
.form-radio {
  @apply h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded;
}

.form-actions {
  @apply flex gap-3 pt-4 border-t border-gray-200;
}

.btn {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}

.btn-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}

.message-history {
  @apply space-y-3 max-h-96 overflow-y-auto pr-2;
}

.message-history-item {
  @apply p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.message-history-item:hover {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}
</style>
